// Constituency Data Service
// Fetches and processes data from the CivIQ Election Data API

class ConstituencyService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
    this.newsApiKey = '98ee93c7c2684dd5a85e7dc7be7e5807';
    this.newsApiURL = 'https://newsapi.org/v2/everything';
    this.cache = new Map();
    this.newsCache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.newsCacheExpiry = 30 * 60 * 1000; // 30 minutes for news
  }

  // Helper method to make API calls
  async apiCall(endpoint) {
    const cacheKey = endpoint;
    const now = Date.now();
    
    // Check cache first
    if (this.cache.has(cacheKey)) {
      const { data, timestamp } = this.cache.get(cacheKey);
      if (now - timestamp < this.cacheExpiry) {
        return data;
      }
    }
    
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'API call failed');
      }
      
      // Cache successful responses
      this.cache.set(cacheKey, { data: result.data, timestamp: now });
      
      return result.data;
    } catch (error) {
      console.error(`API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Get all constituency names for dropdown
  async getConstituencyList() {
    return await this.apiCall('/constituencies');
  }

  // Get detailed data for a specific constituency
  async getConstituencyData(constituencyCode) {
    const rawData = await this.apiCall(`/constituency/${constituencyCode}`);
    return this.transformConstituencyData(rawData);
  }

  // Transform raw API data into dashboard format
  transformConstituencyData(raw) {
    return {
      profile: this.extractProfile(raw),
      demographics: this.extractDemographics(raw),
      voting: this.extractVotingData(raw),
      candidates: this.extractCandidateData(raw),
      results: this.extractResults(raw),
      infrastructure: this.extractInfrastructure(raw)
    };
  }

  // Extract constituency profile information
  extractProfile(data) {
    const { constituency, constituency_code, state, electors } = data;
    const { total, men, women } = electors.total;
    
    return {
      name: this.formatConstituencyName(constituency),
      code: constituency_code,
      state: this.formatStateName(state),
      reservedStatus: this.getReservedStatus(constituency),
      boundaries: `${constituency} constituency area`,
      voters: {
        total: total,
        male: men,
        female: women,
        literacyRate: this.estimateLiteracyRate(data),
        genderRatio: Math.round((women / men) * 1000)
      }
    };
  }

  // Extract demographic breakdown
  extractDemographics(data) {
    const { electors } = data;
    const total = electors.total.total;
    
    // Age group estimation based on typical demographic patterns
    const ageGroups = {
      "18-25": 23,
      "26-40": 35,
      "41-60": 30,
      "60+": 12
    };

    // Caste demographics - estimated from constituency type and state patterns
    const casteDistribution = this.estimateCasteDistribution(data);

    return {
      caste: casteDistribution,
      ageGroups: ageGroups,
      genderDistribution: {
        male: Math.round((electors.total.men / total) * 100),
        female: Math.round((electors.total.women / total) * 100),
        thirdGender: electors.total.third_gender > 0 ? 
          Math.round((electors.total.third_gender / total) * 100) : 0
      },
      migrationImpact: this.estimateMigrationImpact(data)
    };
  }

  // Extract voting patterns and turnout
  extractVotingData(data) {
    const { voters, electors, votes } = data;
    const totalElectors = electors.total.total;
    const totalVotes = voters.general ? voters.general.total : voters.postal_total;
    
    const turnoutPercentage = totalVotes && totalElectors ? 
      Math.round((totalVotes / totalElectors) * 100) : 
      voters.total || 65;

    return {
      turnout: {
        percentage: turnoutPercentage,
        total: totalVotes,
        male: voters.general?.men || Math.round(totalVotes * 0.52),
        female: voters.general?.women || Math.round(totalVotes * 0.48)
      },
      votingMethod: {
        evm: votes.evm_valid || votes.evm_total || 0,
        postal: votes.postal_valid || votes.postal_counted || 0,
        total: votes.total_valid_votes || totalVotes
      },
      nota: votes.nota_votes || 0
    };
  }

  // Extract candidate information
  extractCandidateData(data) {
    const { candidates } = data;
    
    return {
      filed: candidates.nomination_filed,
      rejected: candidates.nomination_rejected,
      withdrawn: candidates.withdrawn,
      contested: candidates.contested,
      forfeitedDeposit: candidates.forfeited_deposit,
      genderBreakdown: {
        men: candidates.contested.men,
        women: candidates.contested.women,
        thirdGender: candidates.contested.third_gender
      }
    };
  }

  // Extract election results
  extractResults(data) {
    const { result, votes } = data;
    
    if (!result || !result.winner) {
      return {
        winner: null,
        runnerUp: null,
        margin: null,
        totalValidVotes: votes.total_valid_votes || 0
      };
    }

    return {
      winner: {
        party: result.winner.party,
        candidate: result.winner.candidate,
        votes: result.winner.votes
      },
      runnerUp: result.runner_up ? {
        party: result.runner_up.party,
        candidate: result.runner_up.candidate,
        votes: result.runner_up.votes
      } : null,
      margin: result.margin,
      totalValidVotes: votes.total_valid_votes || 0,
      voteShare: result.winner.votes && votes.total_valid_votes ? 
        Math.round((result.winner.votes / votes.total_valid_votes) * 100) : null
    };
  }

  // Extract infrastructure data
  extractInfrastructure(data) {
    const { polling_stations, dates } = data;
    
    return {
      pollingStations: {
        total: polling_stations.total,
        averageElectors: polling_stations.average_electors_per_station
      },
      electionDates: {
        polling: dates.polling,
        counting: dates.counting,
        resultDeclaration: dates.result_declaration
      }
    };
  }

  // Utility functions for data transformation
  formatConstituencyName(name) {
    return name.replace(/^\d+-/, '').replace(/-[A-Z]+$/, '').replace(/\([^)]*\)/g, '').trim();
  }

  formatStateName(state) {
    return state.replace(/^S\d+-/, '').trim();
  }

  getReservedStatus(constituency) {
    if (constituency.includes('-SC')) return 'SC';
    if (constituency.includes('-ST')) return 'ST';
    return 'General';
  }

  estimateLiteracyRate(data) {
    const state = this.formatStateName(data.state);
    const stateRates = {
      'Bihar': 63,
      'Uttar Pradesh': 69,
      'West Bengal': 77,
      'Maharashtra': 82,
      'Tamil Nadu': 80,
      'Karnataka': 75
    };
    return stateRates[state] || 70;
  }

  estimateCasteDistribution(data) {
    const reservedStatus = this.getReservedStatus(data.constituency);
    const state = this.formatStateName(data.state);
    
    if (reservedStatus === 'SC') {
      return { SC: 28, OBC: 35, UpperCaste: 20, Muslim: 12, Others: 5 };
    } else if (reservedStatus === 'ST') {
      return { ST: 35, OBC: 30, SC: 15, UpperCaste: 15, Others: 5 };
    } else {
      if (state === 'Bihar') {
        return { OBC: 40, UpperCaste: 25, SC: 16, Muslim: 14, Others: 5 };
      }
      return { OBC: 35, UpperCaste: 30, SC: 18, Muslim: 12, Others: 5 };
    }
  }

  estimateMigrationImpact(data) {
    const urbanKeywords = ['City', 'Urban', 'Metro', 'Nagar'];
    const constituency = data.constituency;
    const isUrban = urbanKeywords.some(keyword => 
      constituency.toLowerCase().includes(keyword.toLowerCase())
    );
    
    if (isUrban) {
      return "High urban in-migration from surrounding rural areas";
    }
    return "Moderate seasonal migration to urban centers for employment";
  }

  // Search constituencies by name
  async searchConstituencies(query) {
    return await this.apiCall(`/constituencies/search?q=${encodeURIComponent(query)}`);
  }

  // Get constituencies by state
  async getConstituenciesByState(state) {
    return await this.apiCall(`/states/${encodeURIComponent(state)}/constituencies`);
  }

  // Get summary statistics
  async getSummaryStats() {
    return await this.apiCall('/summary');
  }

  // Get list of states
  async getStates() {
    return await this.apiCall('/states');
  }

  // Clean constituency name for news search
  cleanConstituencyNameForNews(constituencyName) {
    if (!constituencyName) return '';
    
    // Remove numbering (e.g., "195-")
    let cleaned = constituencyName.replace(/^\d+-/, '');
    
    // Remove constituency type suffixes (-GEN, -SC, -ST)
    cleaned = cleaned.replace(/-(?:GEN|SC|ST)$/, '');
    
    // Remove any parenthetical information
    cleaned = cleaned.replace(/\([^)]*\)/g, '');
    
    // Clean up extra spaces and dashes
    cleaned = cleaned.replace(/[-_]/g, ' ').trim();
    
    return cleaned;
  }

  // Fetch news for a constituency
  async fetchConstituencyNews(constituencyName, limit = 5) {
    const cleanedName = this.cleanConstituencyNameForNews(constituencyName);
    const cacheKey = `news_${cleanedName}`;
    const now = Date.now();
    
    // Check cache first
    if (this.newsCache.has(cacheKey)) {
      const { data, timestamp } = this.newsCache.get(cacheKey);
      if (now - timestamp < this.newsCacheExpiry) {
        return data;
      }
    }
    
    try {
      // Build search query with just the constituency name
      const searchQuery = cleanedName;
      const url = `${this.newsApiURL}?q=${encodeURIComponent(searchQuery)}&apiKey=${this.newsApiKey}&sortBy=publishedAt&pageSize=${limit}&language=en`;

      console.log(`Fetching news for ${cleanedName} from News API: ${url}`);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`News API error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.status !== 'ok') {
        throw new Error(`News API error: ${result.message || 'Unknown error'}`);
      }
      
      // Process and format the news articles
      const processedNews = result.articles.map(article => ({
        id: article.url, // Use URL as unique identifier
        title: article.title,
        description: article.description,
        source: article.source.name,
        author: article.author,
        url: article.url,
        urlToImage: article.urlToImage,
        publishedAt: new Date(article.publishedAt),
        content: article.content,
        sentiment: this.analyzeSentiment(article.title + ' ' + (article.description || '')),
        relevanceScore: this.calculateRelevanceScore(article, cleanedName)
      }));
      
      // Sort by relevance and recency
      const sortedNews = processedNews.sort((a, b) => {
        // First by relevance, then by date
        if (a.relevanceScore !== b.relevanceScore) {
          return b.relevanceScore - a.relevanceScore;
        }
        return b.publishedAt - a.publishedAt;
      });
      
      // Cache the processed news
      this.newsCache.set(cacheKey, { data: sortedNews, timestamp: now });
      
      return sortedNews;
    } catch (error) {
      console.error(`Failed to fetch news for ${cleanedName}:`, error);
      
      // Return fallback news if API fails
      return this.getFallbackNews(cleanedName);
    }
  }

  // Simple sentiment analysis
  analyzeSentiment(text) {
    if (!text) return 'Neutral';
    
    const positiveWords = ['development', 'progress', 'growth', 'improvement', 'success', 'achievement', 'positive', 'good', 'better', 'victory', 'win'];
    const negativeWords = ['problem', 'issue', 'concern', 'crisis', 'protest', 'violence', 'corruption', 'delay', 'failure', 'poor', 'bad'];
    
    const lowerText = text.toLowerCase();
    const positiveCount = positiveWords.filter(word => lowerText.includes(word)).length;
    const negativeCount = negativeWords.filter(word => lowerText.includes(word)).length;
    
    if (positiveCount > negativeCount) return 'Positive';
    if (negativeCount > positiveCount) return 'Negative';
    return 'Neutral';
  }

  // Calculate relevance score for news articles
  calculateRelevanceScore(article, constituencyName) {
    let score = 0;
    const searchTerms = constituencyName.toLowerCase().split(' ');
    const title = (article.title || '').toLowerCase();
    const description = (article.description || '').toLowerCase();
    
    // Higher score for constituency name in title
    searchTerms.forEach(term => {
      if (title.includes(term)) score += 3;
      if (description.includes(term)) score += 1;
    });
    
    // Bonus for election-related content
    const electionTerms = ['election', 'constituency', 'voting', 'voter', 'candidate', 'political'];
    electionTerms.forEach(term => {
      if (title.includes(term)) score += 2;
      if (description.includes(term)) score += 1;
    });
    
    // Recency bonus (newer articles get higher score)
    const daysSincePublished = (Date.now() - new Date(article.publishedAt)) / (1000 * 60 * 60 * 24);
    if (daysSincePublished < 7) score += 2;
    else if (daysSincePublished < 30) score += 1;
    
    return score;
  }

  // Fallback news when API fails
  getFallbackNews(constituencyName) {
    return [
      {
        id: 'fallback_1',
        title: `${constituencyName} Constituency: Voter Registration Drive Underway`,
        description: `Local administration launches comprehensive voter registration campaign in ${constituencyName} to ensure maximum electoral participation.`,
        source: 'Local News',
        author: 'Staff Reporter',
        url: '#',
        urlToImage: null,
        publishedAt: new Date(),
        content: null,
        sentiment: 'Positive',
        relevanceScore: 5,
        isFallback: true
      },
      {
        id: 'fallback_2',
        title: `Development Projects Progress in ${constituencyName}`,
        description: `Infrastructure development initiatives continue to make steady progress across ${constituencyName} constituency.`,
        source: 'Government Updates',
        author: 'Public Relations',
        url: '#',
        urlToImage: null,
        publishedAt: new Date(Date.now() - 24 * 60 * 60 * 1000), // Yesterday
        content: null,
        sentiment: 'Positive',
        relevanceScore: 4,
        isFallback: true
      }
    ];
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Get detailed 2020 election results for a constituency
  async getDetailedResults(constituencyCode) {
    return await this.apiCall(`/constituency/${constituencyCode}/detailed-results`);
  }

  // Get candidate analysis for a constituency
  async getCandidateAnalysis(constituencyCode) {
    return await this.apiCall(`/constituency/${constituencyCode}/candidates`);
  }

  // Get voting analysis for a constituency
  async getVotingAnalysis(constituencyCode) {
    return await this.apiCall(`/constituency/${constituencyCode}/voting-analysis`);
  }

  // Get party performance analysis for a constituency
  async getPartyPerformance(constituencyCode) {
    return await this.apiCall(`/constituency/${constituencyCode}/party-performance`);
  }

  // Get overall election statistics
  async getElectionStatistics() {
    return await this.apiCall('/election-statistics');
  }
}

// Export a singleton instance
export default new ConstituencyService();
